import { CircularProgress, makeStyles, Paper, Theme, useTheme } from '@material-ui/core';
import { TimeSpan, useModePanelStyle } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import moment from 'moment';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { EntityNumericMetricsCustomSettings, EntityUIComponentCollection, EntityUIComponentProps, FetchEntityTimeSpanDataParams,
    filterMetricsDefinitionsByMetricsExclusionCustomSettings, getNumericMetricCustomSettings, isEntityNumericMetricsCustomSettings,
    isFetchEntityTimeRangeDataParams, isMetricsDefinitionAndMetricsExclusionCustomSettings, MetricsDefinitionsAndMetricsExclusionCustomSettings,
    useSelectTranslatedEntityClasses } from '..';
import { NO_VALUE } from '../../../utils/constants';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingStringValue } from '../../../utils/utils';
import { SparklineChart } from '../../time-series';
import { getEntityMetricGroupPreviewData, createFetchEntityMetricDataParamsGroup, MetricPreviewData, KPIStatus, getKPIMetricDefinitions }
    from '../utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            display      : 'flex',
            height       : '100%',
            flexDirection: 'column',
            alignItems   : 'center',
            borderBottom : `6px solid ${theme.palette.grey[500]}`,
            '&.clickable': {
                cursor: 'pointer',
            },

            '&.status-good': {
                borderBottomColor: theme.palette.success.light,
            },

            '&.status-bad': {
                borderBottomColor: theme.palette.error.light,
            },
        },

        panelHeader: {
            display          : 'flex',
            justifyContent   : 'center',
            padding          : theme.spacing(1),
            fontWeight       : 'bold',
            textAlign        : 'center',
            fontSize         : 20,
            width            : '100%',
            borderBottomWidth: 2,
        },

        panelContent: {
            width: '100%',
        },

        metricNameRow: {
            textAlign           : 'center',
            '& .metric-name-col': {
                fontSize: 14,
            },
        },

        metricGroup: {
            '&:not(:last-child)': {
                '& $metricChartAndValueRow': {
                    '& td': {
                        paddingBottom: theme.spacing(1),
                    },
                },
            },
        },

        metricChartAndValueRow: {
            '& .chart-col': {
                marginRight: theme.spacing(2),
                textAlign  : 'right',
                width      : '50%',
                '& .chart' : {
                    display: 'inline-block',
                    width  : 50,
                    height : 25,
                },
            },
            '& .value-col': {
                fontSize    : 20,
                fontWeight  : 'bold',
                textAlign   : 'center',
                whiteSpace  : 'nowrap',
                paddingLeft : theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
            '& .unit-col': {
                width     : '50%',
                fontSize  : 20,
                fontWeight: 'bold',
                textAlign : 'left',
                whiteSpace: 'nowrap',
            },

            '&.status-good': {
                color: theme.palette.success.main,
            },

            '&.status-bad': {
                color: theme.palette.error.main,
            },
        },

        [theme.breakpoints.up('md')]: {
            panelHeader: {
                fontSize: 22,
            },
            metricNameRow: {
                '& .metric-name-col': {
                    fontSize: 14,
                },
            },
            metricGroup: {
                '&:not(:last-child)': {
                    '& $metricChartAndValueRow': {
                        '& td': {
                            paddingBottom: theme.spacing(2),
                        },
                    },
                },
            },
            metricChartAndValueRow: {
                '& .value-col': {
                    fontSize: 25,
                },
            },
        },

        [theme.breakpoints.up('lg')]: {
            panelHeader: {
                fontSize: 25,
            },
            metricNameRow: {
                '& .metric-name-col': {
                    fontSize: 16,
                },
            },
            metricChartAndValueRow: {
                '& .value-col': {
                    fontSize: 30,
                },
            },
        },
    };
}, { name: 'EntityKPIPreviewUIComponent', index: 1 });



/**
 * Custom settings for EntityKPIPreviewUIComponent. These settings can be configured from Entity Class's uiSettings
 */
export interface EntityKPIPreviewUIComponentCustomSettings extends
    MetricsDefinitionsAndMetricsExclusionCustomSettings, EntityNumericMetricsCustomSettings {

    // To customize the title. By default, the entity's name will be displayed.
    readonly titleKey?: string | undefined;

    // Whether or not to show a historical data in a small sparkline chart. The default is TRUE unless specifically set to false
    readonly showHistoricalChart?: boolean | undefined;
}


/**
* type guard function to check if an object is a type of EntityKPIPreviewUIComponentCustomSettings
*/
const isEntityKPIPreviewUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityKPIPreviewUIComponentCustomSettings => {
    const settings = obj as EntityKPIPreviewUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be undefined or a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showHistoricalChart)) {
        return errorLogger?.logError({
            message: '"showHistoricalChart" must be undefined or a boolean.',
            value  : settings.showHistoricalChart,
            path   : [...configPath, 'showHistoricalChart'],
        }) ?? false;
    }

    // Because this component's settings interface extends MetricsExclusionCustomSettings, we can call "isMetricsExclusionCustomSettings"
    // to validate it
    if (!isMetricsDefinitionAndMetricsExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // Because settings is also an EntityNumericMetricsCustomSettings, we can call this type guard function to validate the props defined
    // for "EntityNumericMetricsCustomSettings"
    if (!isEntityNumericMetricsCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};



export interface EntityKPIPreviewUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityKPIPreviewUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent shows a PREVIEW of an Entity KPI's metrics. For each metric, it shows a small sparkline chart of the metric's data
 * and the current value. This component will show the metrics stacked vertically.
 */
export const EntityKPIPreviewUIComponent: React.FC<EntityKPIPreviewUIComponentProps> = (props: EntityKPIPreviewUIComponentProps) => {
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    // For now, this component will only support FetchEntityTimeSpanDataParams. So if the fetchDataParams is another type,
    // we will try to create FetchEntityTimeSpanDataParams using whatever value we have from the fetchDataParams.
    // This component is used for displaying KPI metrics so we might not need to support other fetchDataParams
    const fetchDataParams = useMemo((): FetchEntityTimeSpanDataParams => {
        if (isFetchEntityTimeRangeDataParams(props.fetchDataParams)) {
            return {
                timestamps: [props.fetchDataParams.startTime],
                timeSpan  : TimeSpan.DAY,
            };
        }
        return props.fetchDataParams ? props.fetchDataParams : {
            timeSpan  : TimeSpan.DAY,
            timestamps: [Date.now()],
        };
    }, [props.fetchDataParams]);


    // A local state to keep track of ALL of the entity's KPI metrics' data and status. Each element in this array is an Object that
    // contain the TimeSeries data and status of 1 metric.
    const [metricsPreviewData, setMetricsPreviewData] = useState<readonly MetricPreviewData[] | undefined>();


    const classes = useStyle();
    const panelClasses = useModePanelStyle();
    const theme = useTheme();


    // Find the entity class object that the selectedEntity belonged to
    const selectedEntityClass = useMemo(() => {
        if (entityClasses && selectedEntity) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity?.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);


    /**
     * Once we have the selectedEntityClass, we need to get all the KPI metric definitions from the entity class, the metric definition that has
     * at least 1 KPI numeric metric. All the metrics definition that DOES NOT have at least 1 KPI metrics will be excluded
     */
    const kpiMetricDefinitions = useMemo(() => {
        const kpiMetricsDef = getKPIMetricDefinitions(selectedEntityClass);

        // filter out metrics definitions based on the customSettings' excludeMetricsDefinitionIds
        const filteredMetricsDef = filterMetricsDefinitionsByMetricsExclusionCustomSettings(
            kpiMetricsDef ?? [], props.customSettings,
        );

        return filteredMetricsDef;
    }, [props.customSettings, selectedEntityClass]);


    /**
     * Get the Entity's KPI Status. An Entity's KPI status is based on the status of it's KPI metrics. By default, an Entity has NEUTRAL status.
     * If at least 1 of it's metrics has ABD status, the entity's status will be BAD. If there are no BAD status and one of the metrics has GOOD
     * status then the entity's status will be GOOD.
     */
    const entityStatus = useMemo((): KPIStatus => {
        if (metricsPreviewData) {
            // Find a bad metric
            const badMetric = metricsPreviewData.find((status) => {
                return status.status === KPIStatus.BAD;
            });

            // Even just 1 of the metrics is BAD, this entity's status will be BAD
            if (badMetric) {
                return KPIStatus.BAD;
            }

            // Find a good metric
            const goodMetric = metricsPreviewData.find((status) => {
                return status.status === KPIStatus.GOOD;
            });

            // If there is at least 1 good metric then this entity's status is GOOD
            if (goodMetric) {
                return KPIStatus.GOOD;
            }
        }

        // If data is not loaded or we can't find any BAD or GOOD metric then this entity is considered NEUTRAL
        return KPIStatus.NEUTRAL;
    }, [metricsPreviewData]);



    /**
     * Fetch metric preview data for ALL KPI metrics
     */
    const fetchMetricsPreviewData = useCallback(async (): Promise<readonly MetricPreviewData[] | undefined> => {

        if (selectedEntity && kpiMetricDefinitions) {
            const timeSpan = fetchDataParams.timeSpan ?? TimeSpan.DAY;                                        // default is 1 DAY
            const timestamp = fetchDataParams.timestamps?.[0] ?? moment().startOf(TimeSpan.DAY).valueOf();     // default is TODAY

            // Call 'createFetchEntityMetricDataParams' to create the information we need to fetch metric data. This DOES NOT fetch data,
            // it only return array of objects which contains information we need for making API calls later.
            const multipleFetchDataParamsGroups = createFetchEntityMetricDataParamsGroup(
                kpiMetricDefinitions, selectedEntity, true, props.customSettings,
            );

            // Compile a list of getEntityKPIMetricGroupPreviewData requests
            const requests: Promise<MetricPreviewData[]>[] = [];

            multipleFetchDataParamsGroups.forEach((group) => {
                // Call getEntityKPIMetricGroupPreviewData to fetch data for group of KPI metrics
                requests.push(getEntityMetricGroupPreviewData(group, timestamp, timeSpan, true, props.customSettings?.showHistoricalChart !== false));
            });


            // Resolve all the requests in one call
            const requestResult = await Promise.allSettled(requests).then((allResults) => {
                // Go each result from allResults and get all the result's value for successful result. Return 'undefined' for unsuccessful results
                return allResults.map((singleResult) => {
                    if (singleResult.status === 'fulfilled') {
                        // 'value' is the returned value from the getEntityKPIMetricGroupPreviewData call which will be an array
                        // of MetricPreviewData objects
                        return singleResult.value;
                    }
                    return undefined;
                });
            });

            // Filter out 'undefined' results
            return requestResult
                .flat()                     // requestResult is a double array of MetricPreviewData, 1 per metric so we need to flatten it first
                .filter((result) => {
                    return result !== undefined;
                }) as MetricPreviewData[];
        }

        // Required data is not ready
        return undefined;
    }, [selectedEntity, kpiMetricDefinitions, fetchDataParams.timeSpan, fetchDataParams.timestamps, props.customSettings]);



    /**
     * This useEffect is used for fetching data for all KPI metrics of an Entity
     */
    useEffect(() => {
        fetchMetricsPreviewData().then((data) => {
            setMetricsPreviewData(data);
        });
    }, [fetchMetricsPreviewData]);



    /**
     * Get the color to use for the sparkline chart based on the KPIStatus value
     */
    const getChartColor = useCallback((status: KPIStatus): string => {
        if (status === KPIStatus.GOOD) {
            return theme.palette.success.main;
        }
        if (status === KPIStatus.BAD) {
            return theme.palette.error.main;
        }
        return theme.palette.grey[500];
    }, [theme.palette.error.main, theme.palette.grey, theme.palette.success.main]);


    return (
        <>
            {selectedEntity && selectedEntityClass && (
                <Paper
                    className={clsx(panelClasses.root, classes.root, `status-${entityStatus.toLowerCase()}`, props.onClick && 'clickable')}
                    elevation={2}
                    onClick={props.onClick}
                >
                    <div className={clsx(panelClasses.panelHeader, classes.panelHeader)}>
                        {props.customSettings?.titleKey ? trans(props.customSettings.titleKey) : selectedEntity.displaySettings.name}
                    </div>
                    {metricsPreviewData && (
                        <table className={clsx(panelClasses.panelContent, classes.panelContent)} cellPadding={0} cellSpacing={0}>
                            {metricsPreviewData.map((previewData) => {
                                const statusColor = getChartColor(previewData.status);

                                const numericMetricSettings = getNumericMetricCustomSettings(
                                    previewData.metric.name,
                                    previewData.metricDefinition.metricsDefinitionId,
                                    props.customSettings,
                                );

                                return (
                                    <tbody
                                        key={`${previewData.metricDefinition.metricsDefinitionId}-${previewData.metric.name}`}
                                        className={classes.metricGroup}
                                    >
                                        <tr className={classes.metricNameRow}>
                                            <td className="metric-name-col" colSpan={3}>
                                                {previewData.metric.evaluator?.displayName || previewData.metric.displayName}
                                            </td>
                                        </tr>
                                        <tr className={clsx(classes.metricChartAndValueRow, `status-${previewData.status.toLowerCase()}`)}>
                                            <td className="chart-col">
                                                {props.customSettings?.showHistoricalChart !== false && previewData.dataPoints.length > 0 && (
                                                    <SparklineChart
                                                        className="chart"
                                                        dataPoints={previewData.dataPoints}
                                                        metrics={previewData.metric}
                                                        color={statusColor}
                                                    />
                                                )}
                                            </td>
                                            <td className="value-col">
                                                {previewData.currentValue !== undefined
                                                    ? trans('{{value, number-format}}', {
                                                        value        : previewData.currentValue,
                                                        decimalPlaces: numericMetricSettings?.decimalPlaces,
                                                    }) : NO_VALUE}
                                            </td>
                                            <td className="unit-col">
                                                {previewData.metric.unit}
                                            </td>
                                        </tr>
                                    </tbody>
                                );
                            })}
                        </table>
                    )}
                </Paper>
            )}

            {!(selectedEntity || selectedEntityClass) && (
                <CircularProgress />
            )}
        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntityKPIPreviewUIComponent', EntityKPIPreviewUIComponent);


// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_KPI_Preview_Comp',
    {
        componentFunc          : EntityKPIPreviewUIComponent,
        customSettingsValidator: isEntityKPIPreviewUIComponentCustomSettings,
    },
);
